import React, { CSSProperties } from 'react';

import classNames from 'classnames';
import type { Dictionary } from 'lodash';
import type { CellProps } from 'react-table';
import type { DeliveryBlock, Driver, OPTIMIZATION_TYPE, Vehicle, VehicleType } from '@bringg/types';
import type { EtaResponseRouteItem } from '@bringg-frontend/eta-calculator';
import ColorPicker from '@bringg/react-components/dist/components/color-picker/color-picker';
import { getLocalizedDistanceMessage } from '@bringg/frontend-utils';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { getTaskInventories } from '@bringg-frontend/shared-tasks-weight';

import type { RouteRowTranslations, TableHeadersTranslation } from '../../../../components/routes-planner/translations';
import { colorList } from '../../../../utils/color-generator';
import {
	RoutesPlannerConfiguration,
	ROUTE_PLANNER_CLASS_NAME
} from '../../../../components/routes-planner/routes-planner';
import { NO_ROUTE_ID } from '../../../../stores/routes-planner-store';
import type { RoutesPlannerDeps, RoutesPlannerRun, RoutesPlannerTask } from '../../../../types';
import { UpdatingSource } from '../../../types';
import { isRouteExistsAndUnassigned } from '../../../routes-planner/utils';
import SelectCell from './cells/select-cell/select-cell';
import DriverCell from './cells/driver-cell/driver-cell';
import DurationCell from './cells/duration-cell/duration-cell';
import RunTimesCell from './cells/run-times-cell/run-times-cell';
import WeightCell from './cells/weight-cell/weight-cell';
import HandlingUnitsCell from './cells/handling-units-cell/handling-units-cell';
import VehicleCell from './cells/vehicle-cell/vehicle-cell';
import { OpenUserAssignerProps } from '../../../../types/routes-planner-app';
import { UpdateVehicleOnRunProps } from '../../../../types/routes-planner-app';

import './route-row.scss';

export interface Props {
	runToTasksInPresentedDate: Dictionary<RoutesPlannerTask[]>;
	cellProps: CellProps<RoutesPlannerTask>;
	color: string;
	routesById: Dictionary<RoutesPlannerRun>;
	headers: TableHeadersTranslation;
	name: string;
	allConfiguration: RoutesPlannerConfiguration;
	shouldShowVehicles: boolean;
	shouldShowTrailer: boolean;
	checkListAmount: number;
	isExpanded?: boolean;
	translations: RouteRowTranslations;
	onColorChange?: (color: string) => void;
	optimizationType: OPTIMIZATION_TYPE;
	timezone?: string;
	tasksById: Map<number, RoutesPlannerTask>;
	selectedTasksIds: number[];
	changeTasksSelection: (tasksIds: number[], source: UpdatingSource) => void;
	runToDriver: Dictionary<Driver>;
	vehiclesById: Dictionary<Vehicle>;
	routeInfoByIndex: Dictionary<EtaResponseRouteItem>;
	deps: RoutesPlannerDeps;
	vehicleTypeByVehicleIds: Dictionary<VehicleType>;
	isRunModified: (runId: number) => boolean;
	customerIdByTaskInventory: { [taskInventoryId: string]: number };
	deliveryBlocksByDriver: { [userId: number]: DeliveryBlock[] };
	deliveryBlocksByVehicle: { [vehicleId: number]: DeliveryBlock[] };
	openUserAssignerModal?: (props: OpenUserAssignerProps) => void;
	openVehicleAssignerModal?: (
		runId: number,
		onClose: ({ runId, vehicleId, tasksIds }: UpdateVehicleOnRunProps) => void
	) => void;
}
const RouteRow: React.FC<Props> = ({
	color,
	name,
	shouldShowVehicles,
	shouldShowTrailer,
	allConfiguration,
	checkListAmount,
	isExpanded,
	translations,
	onColorChange,
	timezone,
	cellProps,
	tasksById,
	selectedTasksIds,
	changeTasksSelection,
	routesById,
	optimizationType,
	headers,
	runToDriver,
	openUserAssignerModal,
	openVehicleAssignerModal,
	vehiclesById,
	routeInfoByIndex,
	deps,
	vehicleTypeByVehicleIds,
	isRunModified,
	customerIdByTaskInventory,
	deliveryBlocksByDriver,
	deliveryBlocksByVehicle
}) => {
	const {
		enableVehicles,
		hourFormat,
		isClusterMode,
		ignoreQuantityInLoadCalculation,
		uniqueInventoryFields,
		dynamicCapacityMode,
		initialLoadingTimeMinutes,
		countryCode
	} = allConfiguration;

	const { value: runId, row, onSelect } = cellProps;
	const run = routesById[row.values.run_id];
	const { checked, indeterminate, style } = row.getToggleGroupSelectedProps();

	const allowColorChange = checked && runId !== NO_ROUTE_ID;

	const isLocked = isRouteExistsAndUnassigned(run, optimizationType);

	const driverName = runId !== NO_ROUTE_ID ? runToDriver[runId]?.name || headers.unassigned : null;

	const driverRank = runId !== NO_ROUTE_ID ? runToDriver[runId]?.rank : 0;

	const distanceData = {
		transportDistance: routeInfoByIndex[runId]?.transport_distance || routesById[runId]?.transport_distance,
		countryCode: countryCode
	};

	const tasksInventories = getTaskInventories(
		row.subRows.map(subRow => subRow.original),
		isClusterMode,
		customerIdByTaskInventory
	);

	return (
		<div className="route-row" style={{ ...style, '--color': isLocked ? 'white' : color } as CSSProperties}>
			{allowColorChange ? (
				<ColorPicker
					className="color-mark"
					data-test-id="color-mark"
					colors={colorList}
					selectedIndex={colorList.indexOf(color)}
					onSelect={onColorChange}
					placement="rightBottom"
					getPopupContainer={() => document.querySelector(`.${ROUTE_PLANNER_CLASS_NAME}`)}
				/>
			) : (
				<div className="color-mark" />
			)}

			<div className="route-row-item row-arrow">
				<BringgIcon
					iconName={BringgFontIcons.ChevronRight}
					className={classNames({ 'is-expanded': isExpanded })}
				/>
			</div>

			<SelectCell
				isLocked={isLocked}
				translations={translations}
				checked={checked}
				indeterminate={indeterminate}
				runId={row.values.run_id !== null ? row.values.run_id : NO_ROUTE_ID}
				tasksById={tasksById}
				selectedTasksIds={selectedTasksIds}
				changeTasksSelection={changeTasksSelection}
				onSelect={onSelect}
			/>

			<div className="route-row-item name">{<span className="component-route-name">{name}</span>}</div>

			<DriverCell
				driverName={driverName}
				runId={runId}
				translations={translations}
				openUserAssignerModal={openUserAssignerModal}
			/>

			{shouldShowVehicles && (
				<VehicleCell
					run={run}
					vehicleType="vehicle"
					canUpdateVehicle={!!Object.keys(vehiclesById).length}
					openVehicleAssignerModal={openVehicleAssignerModal}
					unassignTranslation={headers.unassigned}
				/>
			)}

			{shouldShowVehicles && shouldShowTrailer && (
				<VehicleCell
					type="trailer-name"
					run={run}
					vehicleType="trailer"
					canUpdateVehicle={!!Object.keys(vehiclesById).length}
					openVehicleAssignerModal={openVehicleAssignerModal}
					unassignTranslation={headers.unassigned}
				/>
			)}

			<div className="route-row-item checklist">
				<span className="route-row-item-checklist-value">{checkListAmount}</span>
			</div>
			<div className="route-row-item duration-column">
				{runId !== NO_ROUTE_ID ? (
					<DurationCell
						run={routesById[runId]}
						deliveryBlocksByDriver={deliveryBlocksByDriver}
						deliveryBlocksByVehicle={deliveryBlocksByVehicle}
						initialLoadingTimeMinutes={initialLoadingTimeMinutes}
					/>
				) : (
					'-'
				)}
			</div>

			<RunTimesCell
				routeInfoByIndex={routeInfoByIndex}
				routesById={routesById}
				runId={runId}
				hourFormat={hourFormat}
				timeZone={timezone}
			/>

			<div className="route-row-item transport-distance">
				{runId !== NO_ROUTE_ID && distanceData.transportDistance > 0 ? (
					<span className="transport-distance-text">
						{getLocalizedDistanceMessage(distanceData.transportDistance * 1000, distanceData.countryCode, {
							miles: translations.miles,
							km: translations.km
						})}
					</span>
				) : (
					'-'
				)}
			</div>

			<div className="route-row-item weight">
				{runId !== NO_ROUTE_ID ? (
					<WeightCell
						isRunModified={isRunModified}
						runId={runId}
						run={run}
						tasksInventories={tasksInventories}
						ignoreQuantityInLoadCalculation={ignoreQuantityInLoadCalculation}
						uniqueInventoryFields={uniqueInventoryFields}
						dynamicCapacityMode={dynamicCapacityMode}
						routesById={routesById}
						getUserType={deps.getUserType}
						subRows={row.subRows}
						vehicleTypeByVehicleIds={vehicleTypeByVehicleIds}
						enableVehicles={enableVehicles}
						translations={translations}
					/>
				) : (
					'-'
				)}
			</div>

			<div data-test-id="handling-units" className="route-row-item handling-units">
				<HandlingUnitsCell
					tasksInventories={tasksInventories}
					translations={translations}
					tasks={row.subRows}
					runId={runId}
					run={run}
				/>
			</div>

			<div className="route-row-item driver-rank">{runId !== NO_ROUTE_ID ? driverRank : '-'}</div>
		</div>
	);
};

export default RouteRow;
